import React from 'react';
import { OnboardingStep } from './OnboardingStep';
import {
  useFetchOnboardingSteps,
  useSkipOnboardingStep,
} from '@shared/hooks/onboardings';
import { useAppSelector } from '@shared/hooks/use-app-selector';

import { isResellerAccount } from '@shared/utils';
import { getAppSettings } from '../../../selectors';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import { useDispatch } from 'react-redux';
import { changeLocation } from '../../../actions';
import EVENT_BUS_EVENTS from '../../../constants/event-bus-events';

const STEP_ROUTES = {
  setup_address: '/settings',
  create_service: '/services',
  create_appointment: '/calendar',
  activate_subscription: '/settings/subscription',
  setup_shore_pay: '/application-settings/payments',
} as const;

export function OnboardingSteps() {
  const dispatch = useDispatch();
  const { trackMethod, eventBus } = useGlobals();
  const { data: onboardingStepsData, isLoading: isLoadingOnboardingSteps } =
    useFetchOnboardingSteps();

  const { mutate: mutateSkipOnboardingStep } = useSkipOnboardingStep();

  const percentageCompleted =
    onboardingStepsData?.overall_progress_percentage ?? 0;

  const { subscriptionPartner } =
    useAppSelector((state) => getAppSettings(state)) || {};

  const isReseller = isResellerAccount(subscriptionPartner);

  const filteredOnboardingSteps = onboardingStepsData
    ? {
        ...onboardingStepsData,
        steps: onboardingStepsData?.steps?.filter((step) =>
          isReseller ? step.id !== 'activate_subscription' : true
        ),
      }
    : undefined;

  const showOnboardingSteps =
    (filteredOnboardingSteps?.overall_progress_percentage ?? 100) < 100;

  const handleOnStepClick = (stepId: string) => {
    trackMethod('onboarding_step_clicked', {
      platform: 'booking',
      step_id: stepId,
      overall_progress_percentage: percentageCompleted,
    });

    const route = STEP_ROUTES[stepId as keyof typeof STEP_ROUTES];

    if (route) {
      dispatch(changeLocation(route));
      eventBus.emit(EVENT_BUS_EVENTS.NAVIGATE, {
        payload: { route },
      });
    }
  };

  const handleOnSkipStepClick = (stepId: string) => {
    trackMethod('onboarding_skip_step_clicked', {
      platform: 'booking',
      overall_progress_percentage: percentageCompleted,
    });
    mutateSkipOnboardingStep();
  };

  return (
    <div className="gv-surface-bright gv-border gv-radius">
      {!isLoadingOnboardingSteps &&
        filteredOnboardingSteps?.steps.map((step, index) => (
          <OnboardingStep
            key={step.id}
            id={step.id}
            title={step.title}
            description={step.description}
            isMandatory={step.is_mandatory}
            completedAt={step.completed_at}
            isLastStep={index === filteredOnboardingSteps?.steps?.length - 1}
            onStepClick={handleOnStepClick}
            onSkipStepClick={handleOnSkipStepClick}
          />
        ))}
    </div>
  );
}
